// DOC-DEPS: LLM.md -> src/features/profile/README.md
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { InfoSheetPanel } from './InfoSheetPanel';
import { useAuthStore } from '../../../store/useAuthStore';
import { submitFeedback } from '../../../services/feedback/submitFeedback';
import { APP_PROFILE_JELLY_BUTTON_STYLE } from '../../../lib/modalTheme';
import { AppSelectMenu } from '../../../components/AppSelectMenu';

const ISSUE_TYPES = [
  'feedback_type_bug',
  'feedback_type_account',
  'feedback_type_subscription',
  'feedback_type_feature',
  'feedback_type_data',
  'feedback_type_other',
] as const;

interface Props {
  onClose: () => void;
}

export const FeedbackPanel: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);

  const [email, setEmail] = useState(user?.email ?? '');
  const [subject, setSubject] = useState('');
  const [issueType, setIssueType] = useState('');
  const [desc, setDesc] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim() || !subject.trim() || !issueType || !desc.trim()) {
      setError(true);
      return;
    }
    setError(false);
    setLoading(true);
    try {
      await submitFeedback({
        userId: user?.id ?? '',
        email: email.trim(),
        subject: subject.trim(),
        issueType,
        description: desc.trim(),
      });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <InfoSheetPanel title={t('feedback_sheet_title')} onClose={onClose}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 size={48} strokeWidth={1.5} className="mb-4 text-[#5F7A63]" />
          <h2 className="app-section-title mb-2 text-slate-800">{t('feedback_success_title')}</h2>
          <p className="app-body mb-8 leading-relaxed text-slate-500">{t('feedback_success_body')}</p>
          <button
            type="button"
            onClick={onClose}
            className="app-body rounded-[50px] px-8 py-3 font-semibold text-[#5F7A63] transition hover:opacity-90"
            style={APP_PROFILE_JELLY_BUTTON_STYLE}
          >
            {t('feedback_success_close')}
          </button>
        </div>
      </InfoSheetPanel>
    );
  }

  return (
    <InfoSheetPanel title={t('feedback_sheet_title')} onClose={onClose}>
      <p className="app-body mb-5 leading-relaxed text-slate-500">{t('feedback_intro')}</p>

      <div className="space-y-4">
        {/* Email */}
        <Field label={t('feedback_email_label')} required>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('feedback_email_placeholder')}
            className={inputCls(error && !email.trim())}
          />
        </Field>

        {/* Subject */}
        <Field label={t('feedback_subject_label')} required>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder={t('feedback_subject_placeholder')}
            className={inputCls(error && !subject.trim())}
          />
        </Field>

        {/* Issue type dropdown */}
        <Field label={t('feedback_type_label')} required>
          <AppSelectMenu
            value={issueType}
            options={ISSUE_TYPES.map(key => ({ value: key, label: t(key) }))}
            onChange={setIssueType}
            ariaLabel={t('feedback_type_label')}
            placeholder={t('feedback_type_placeholder')}
            invalid={error && !issueType}
          />
        </Field>

        {/* Description */}
        <Field label={t('feedback_desc_label')} required>
          <textarea
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder={t('feedback_desc_placeholder')}
            rows={5}
            className={`${inputCls(error && !desc.trim())} resize-none leading-relaxed`}
          />
        </Field>

        {/* Error hint */}
        {error && (
          <p className="app-caption text-red-500">{t('feedback_required')}</p>
        )}

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="app-body w-full rounded-[50px] py-3 font-semibold text-[#5F7A63] transition hover:opacity-90 disabled:opacity-50"
          style={APP_PROFILE_JELLY_BUTTON_STYLE}
        >
          {loading ? '...' : t('feedback_submit')}
        </button>
      </div>

      <div className="h-8" />
    </InfoSheetPanel>
  );
};

const inputCls = (hasError: boolean) =>
  `app-form-text w-full rounded-xl border px-3.5 py-2.5 text-slate-800 placeholder-slate-400 outline-none transition-shadow ${
    hasError
      ? 'border-red-300 bg-red-50/60 focus:ring-2 focus:ring-red-200'
      : 'border-[#8FAF92]/40 bg-white/85 focus:border-[#8FAF92]/70 focus:ring-2 focus:ring-[#8FAF92]/25'
  }`;

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <label className="app-body flex items-center gap-1 font-medium text-slate-700">
      {label}
      {required && <span className="text-red-400">*</span>}
    </label>
    {children}
  </div>
);
